import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { GetAction } from '../../Component/GetAction'
import { PostAction } from '../../Component/PostAction'
import ManageTable from '../ManageTable'
import { RequireModule, PageHeader } from './shared'

function userDisplayName(u) {
  if (!u) return ''
  return (
    u.name ||
    `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
    u.email_address ||
    ''
  )
}

function RolesPage() {
  const [modules, setModules] = useState([])
  const [roles, setRoles] = useState([])
  const [users, setUsers] = useState([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)

  const [form, setForm] = useState({
    id: 0,
    name: '',
    description: '',
    status: 1,
    modules: [],
  })

  const [assign, setAssign] = useState({
    user_id: '',
    roles: [],
  })
  const [userQuery, setUserQuery] = useState('')
  const [userOpen, setUserOpen] = useState(false)

  const actorId = String(sessionStorage.getItem('id') || '')

  const loadAll = useCallback(async () => {
    setLoading(true)
    const [modRes, roleRes, userRes] = await Promise.all([
      GetAction('get', {}, 'roles/modules'),
      GetAction('get', {}, 'roles/roleList'),
      GetAction('get', {}, 'user/userList'),
    ])
    if (modRes?.status === 'success' && Array.isArray(modRes.response)) {
      setModules(modRes.response)
    }
    if (roleRes?.status === 'success' && Array.isArray(roleRes.response)) {
      setRoles(roleRes.response)
      setError('')
    } else {
      setError(roleRes?.message || 'Failed to load roles.')
      setRoles([])
    }
    if (userRes?.status === 'success' && Array.isArray(userRes.response)) {
      setUsers(userRes.response)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const labelMap = Object.fromEntries(modules.map((m) => [m.key, m.label]))
  const isAdminRole = Number(form.id) === 1
  const isSystemRole = roles.some(
    (r) => Number(r.id) === Number(form.id) && Number(r.is_system) === 1
  )
  const isSelfAssign = actorId && String(assign.user_id) === actorId
  const selectedUser = users.find((u) => String(u.id) === String(assign.user_id))

  const userMatches = useMemo(() => {
    const q = userQuery.trim().toLowerCase()
    if (!q) return users.slice(0, 12)
    return users
      .filter((u) => {
        const name = userDisplayName(u).toLowerCase()
        const mobile = String(u.mobile || '').toLowerCase()
        const email = String(u.email_address || '').toLowerCase()
        const role = String(u.role_name || '').toLowerCase()
        return (
          name.includes(q) ||
          mobile.includes(q) ||
          email.includes(q) ||
          role.includes(q)
        )
      })
      .slice(0, 12)
  }, [users, userQuery])

  const openNew = () => {
    setForm({ id: 0, name: '', description: '', status: 1, modules: [] })
    setShowForm(true)
  }

  const openEdit = (role) => {
    setForm({
      id: role.id,
      name: role.name || '',
      description: role.description || '',
      status: Number(role.status) === 1 ? 1 : 0,
      modules: Array.isArray(role.modules) ? [...role.modules] : [],
    })
    setShowForm(true)
  }

  const toggleModule = (key) => {
    if (isAdminRole) return
    setForm((prev) => {
      const has = prev.modules.includes(key)
      return {
        ...prev,
        modules: has ? prev.modules.filter((k) => k !== key) : [...prev.modules, key],
      }
    })
  }

  const saveRole = async (e) => {
    e.preventDefault()
    setSaving(true)
    setMessage(null)
    const payload = {
      id: form.id || 0,
      name: form.name.trim(),
      description: form.description.trim(),
      status: Number(form.status),
      modules: Number(form.id) === 1 ? modules.map((m) => m.key) : form.modules,
    }
    const data = await PostAction('post', payload, 'roles/addRole')
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Role saved.' })
      setShowForm(false)
      await loadAll()
    } else {
      setMessage({ error: true, text: data?.message || 'Failed to save role.' })
    }
    setSaving(false)
  }

  const deleteRole = async (id) => {
    if (!window.confirm('Delete this role?')) return
    const data = await PostAction('post', { id: Number(id) }, 'roles/deleteRole')
    setMessage({
      error: data?.status !== 'success',
      text: data?.message || (data?.status === 'success' ? 'Deleted.' : 'Failed.'),
    })
    if (data?.status === 'success') {
      setShowForm(false)
      await loadAll()
    }
  }

  const pickUser = (user) => {
    const roleIds = Array.isArray(user?.role_ids)
      ? user.role_ids.map(String)
      : user?.user_role
        ? [String(user.user_role)]
        : []
    setAssign({ user_id: String(user.id), roles: roleIds })
    setUserQuery(userDisplayName(user))
    setUserOpen(false)
  }

  const clearUser = () => {
    setAssign({ user_id: '', roles: [] })
    setUserQuery('')
  }

  const saveAssign = async (e) => {
    e.preventDefault()
    if (isSelfAssign) return
    if (!assign.user_id) {
      setMessage({ error: true, text: 'Please select a user.' })
      return
    }
    setSaving(true)
    setMessage(null)
    const data = await PostAction(
      'post',
      {
        user_id: Number(assign.user_id),
        roles: assign.roles.map(Number),
        actor_id: Number(actorId),
      },
      'roles/assignRole'
    )
    if (data?.status === 'success') {
      setMessage({ error: false, text: data.message || 'Roles assigned.' })
      await loadAll()
    } else {
      setMessage({ error: true, text: data?.message || 'Failed to assign roles.' })
    }
    setSaving(false)
  }

  const columns = [
    { key: 'index', label: '#', width: '50px' },
    {
      key: 'name',
      label: 'Name',
      render: (row) => (
        <span>
          <strong>{row.name}</strong>
          {Number(row.is_system) === 1 ? (
            <span className="badge badge-secondary ml-2">System</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'description',
      label: 'Description',
      render: (row) => row.description || '—',
    },
    {
      key: 'modules',
      label: 'Modules',
      render: (row) =>
        (row.modules || []).map((k) => labelMap[k] || k).join(', ') || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (Number(row.status) === 1 ? 'Active' : 'Inactive'),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="d-flex">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary mr-2"
            onClick={() => openEdit(row)}
          >
            Edit
          </button>
          {Number(row.is_system) !== 1 ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => deleteRole(row.id)}
            >
              Delete
            </button>
          ) : null}
        </div>
      ),
    },
  ]

  return (
    <RequireModule moduleKey="user_roles">
      <PageHeader
        title="User Roles"
        subtitle="Roles, module permissions, and user assignment."
        actions={
          <button
            type="button"
            className="btn btn-sm btn-primary"
            style={{ backgroundColor: '#fd7e14', borderColor: '#fd7e14' }}
            onClick={openNew}
          >
            Add Role
          </button>
        }
      />
      {message ? (
        <div
          className={`manage-alert ${message.error ? 'manage-alert-err' : 'manage-alert-ok'}`}
        >
          {message.text}
        </div>
      ) : null}
      {error ? <div className="manage-alert manage-alert-err">{error}</div> : null}

      <div className="manage-card mb-4">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : (
          <ManageTable
            columns={columns}
            rows={roles}
            searchKeys={['name', 'description']}
          />
        )}
      </div>

      {showForm ? (
        <div className="manage-card mb-4">
          <h2 className="h5 mb-3">{form.id ? 'Edit Role' : 'Add Role'}</h2>
          <form onSubmit={saveRole}>
            <div className="mb-3">
              <label className="form-label" htmlFor="role_name">
                Name
              </label>
              <input
                id="role_name"
                className="form-control"
                required
                disabled={isSystemRole}
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="role_desc">
                Description
              </label>
              <textarea
                id="role_desc"
                className="form-control"
                rows={2}
                value={form.description}
                onChange={(e) =>
                  setForm((p) => ({ ...p, description: e.target.value }))
                }
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="role_status">
                Status
              </label>
              <select
                id="role_status"
                className="form-control"
                value={form.status}
                onChange={(e) =>
                  setForm((p) => ({ ...p, status: Number(e.target.value) }))
                }
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label mb-0">Modules</label>
                {!isAdminRole ? (
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          modules: modules.map((m) => m.key),
                        }))
                      }
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setForm((p) => ({ ...p, modules: [] }))}
                    >
                      Clear
                    </button>
                  </div>
                ) : null}
              </div>
              {isAdminRole ? (
                <p className="small text-muted">
                  Administrator always has access to every module.
                </p>
              ) : null}
              <div className="manage-module-grid">
                {modules.map((m) => (
                  <label key={m.key} className="manage-module-check">
                    <input
                      type="checkbox"
                      checked={isAdminRole || form.modules.includes(m.key)}
                      disabled={isAdminRole}
                      onChange={() => toggleModule(m.key)}
                    />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="d-flex">
              <button
                type="submit"
                className="btn btn-primary mr-2"
                style={{ backgroundColor: '#fd7e14', borderColor: '#fd7e14' }}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Role'}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="manage-card">
        <h2 className="h5 mb-3">Assign roles to user</h2>
        <form onSubmit={saveAssign}>
          <div className="mb-3">
            <label className="form-label" htmlFor="assign_user">
              User
            </label>
            <div className="manage-typeahead">
              <input
                id="assign_user"
                className="form-control"
                autoComplete="off"
                placeholder="Search name, mobile, email, or role…"
                value={
                  userOpen || !selectedUser
                    ? userQuery
                    : `${userDisplayName(selectedUser)}${
                        selectedUser.role_name ? ` — ${selectedUser.role_name}` : ''
                      }`
                }
                onChange={(e) => {
                  setUserQuery(e.target.value)
                  setAssign({ user_id: '', roles: [] })
                  setUserOpen(true)
                }}
                onFocus={() => setUserOpen(true)}
                onBlur={() => setTimeout(() => setUserOpen(false), 150)}
                required={!assign.user_id}
              />
              {userOpen ? (
                <div className="manage-typeahead-menu">
                  {userMatches.length === 0 ? (
                    <div className="px-3 py-2 text-muted small">No matches</div>
                  ) : (
                    userMatches.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        className="manage-typeahead-item"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pickUser(u)}
                      >
                        {userDisplayName(u)}
                        {u.mobile ? ` · ${u.mobile}` : ''}
                        {u.role_name ? ` — ${u.role_name}` : ''}
                      </button>
                    ))
                  )}
                </div>
              ) : null}
            </div>
            {assign.user_id ? (
              <button
                type="button"
                className="btn btn-link btn-sm px-0 mt-1"
                onClick={clearUser}
              >
                Clear selection
              </button>
            ) : null}
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="assign_roles">
              Roles
            </label>
            <select
              id="assign_roles"
              className="form-control"
              multiple
              size={Math.min(6, Math.max(3, roles.filter((r) => Number(r.status) === 1).length))}
              disabled={isSelfAssign}
              value={assign.roles}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions).map((o) => o.value)
                setAssign((p) => ({ ...p, roles: selected }))
              }}
            >
              {roles
                .filter((r) => Number(r.status) === 1)
                .map((r) => (
                  <option key={r.id} value={String(r.id)}>
                    {r.name}
                  </option>
                ))}
            </select>
            <div className="form-text">Hold Ctrl/Cmd to select multiple roles.</div>
          </div>
          {isSelfAssign ? (
            <p className="small text-danger">You cannot change your own roles here.</p>
          ) : null}
          <button
            type="submit"
            className="btn btn-primary"
            style={{ backgroundColor: '#fd7e14', borderColor: '#fd7e14' }}
            disabled={saving || isSelfAssign || !assign.user_id}
          >
            {saving ? 'Saving…' : 'Assign Roles'}
          </button>
        </form>
      </div>
    </RequireModule>
  )
}

export default RolesPage
